// Normalisation des noms d'exercices JSON programme → noms canoniques de la table
// `exercises`, et conversion de la charge (%1RM) en kg. Module pur, sans effet de
// bord (pas d'import Supabase) afin de pouvoir être exécuté aussi bien côté app que
// depuis scripts/diagnose-exercise-names.js (audit hors-app via esbuild).

/**
 * Maps JSON programme exercise names to canonical names in the `exercises` table.
 * Keys are the raw strings that appear in JSON data; values are exact table names.
 */
// Motif de répétitions en fin d'item d'échauffement : "2x10", "3 × 12", avec ce qui le
// qualifie ("/côté", "''", "[Variante…]"). C'est ce motif, et lui seul, qui sépare le nom
// des reps — couper au premier mot commençant par un chiffre amputait les noms qui en
// contiennent un ("Dynamic 90-90 Hip Twist 2x8" devenait "Dynamic", et
// "Cable Shoulder 90 Degree Internal 2x15" devenait "Cable Shoulder").
const WARMUP_REPS_RE = /^(.+?)\s+(\d+\s*[x×]\s*\d+.*)$/i

/**
 * Découpe un libellé d'échauffement en nom + reps ("PVC Pass Through 2x10").
 *
 * Vit ici, dans le seul module sans dépendances, pour que `scripts/migrate-exercise-ids.js`
 * découpe EXACTEMENT comme l'écran : le nom qu'il résout en `exercise_id` doit être celui
 * que l'app affiche, sinon les deux divergent au premier ajustement du motif.
 */
/**
 * Sépare un libellé de sa VARIANTE entre crochets — "[Variante maison : … si pas de
 * rameur]", "[Variante bibliothèque : …]".
 *
 * Elle ne dépend PAS du lieu d'entraînement : ces crochets n'existent que dans les
 * programmes maison (la scission par lieu est déjà faite à la source), et la condition
 * qu'ils portent est le MATÉRIEL du client — « si pas de rameur » —, que le profil ne
 * déclare pas. La variante n'est donc jamais masquée : elle passe sur une ligne à part,
 * sous la consigne principale, plutôt que de s'y fondre au milieu.
 *
 * Le texte de la variante est conservé mot pour mot, condition comprise ; seul le
 * préfixe redondant ("Variante maison :") tombe, puisque la ligne est déjà étiquetée.
 */
export function splitWarmupVariant(label: string): { main: string; variant: string | null } {
  const m = /\[([^\]]*)\]/.exec(label)
  if (!m) return { main: label.trim(), variant: null }
  const inner = m[1].replace(/^\s*Variante\s+\w+\s*:\s*/i, "").trim()
  const main = (label.slice(0, m.index) + label.slice(m.index + m[0].length)).replace(/\s+/g, " ").trim()
  return { main, variant: inner || null }
}

export function parseWarmupLabel(raw: string): { name: string; reps: string } {
  const trimmed = raw.trim()
  const reps = WARMUP_REPS_RE.exec(trimmed)
  if (reps) return { name: reps[1].trim(), reps: reps[2].trim() }
  // Repli : aucun motif "NxM" (tous les programmes actuels en ont un, sauf le cardio,
  // qui est une phrase). On retombe sur l'ancienne règle — premier mot commençant par
  // un chiffre — qui couvre les autres notations de reps ("Hip Circles 10/sens").
  const m = /^(.+?)\s+(\d.*)$/.exec(trimmed)
  if (m) return { name: m[1].trim(), reps: m[2].trim() }
  return { name: trimmed, reps: "" }
}

export const EXERCISE_ALIAS_MAP: Record<string, string> = {
  // DB abbreviation → Dumbbell
  "Incline DB Curl":                     "Incline Dumbbell Curl",
  "Incline DB Bench Press":              "Incline Dumbbell Press",
  "Incline DB Press":                    "Incline Dumbbell Press",
  "Incline DB Fly":                      "Incline Dumbbell Fly",
  "Dumbbell Flat Bench Press":           "Dumbbell Bench Press",
  "DB Bench Press":                      "Dumbbell Bench Press",
  "DB Floor Press":                      "Dumbbell Floor Press",
  "DB Romanian Deadlift":                "Dumbbell Romanian Deadlift",
  "DB Shoulder Press":                   "Dumbbell Shoulder Press",
  "DB Row":                              "Dumbbell Row",
  // Barbell naming variants
  "Barbell Hip Thrust":                  "Hip Thrust",
  "Barbell Incline Press":               "Incline Barbell Bench Press",
  "Bardell Incline Press":               "Incline Barbell Bench Press",
  // Pulldown typo
  "Lateral Pulldown":                    "Lat Pulldown",
  // EZ Bar variants
  "EZ-Bar Curl":                         "EZ Bar Curl",
  "EZ-Bar Biceps Curl":                  "EZ Bar Curl",
  "EZ Bar Biceps Curl":                  "EZ Bar Curl",
  // Rear Delt Fly — all equipment variants → single table entry
  "Cable Rear Delt Fly":                 "Rear Delt Fly",
  "DB Rear Delt Fly":                    "Rear Delt Fly",
  "Dumbbell Rear Delt Fly":              "Rear Delt Fly",
  "Dumbbell Rear Fly buste penché":      "Rear Delt Fly",
  "Band Rear Delt Fly":                  "Rear Delt Fly",
  "Bent-Over Reverse Fly haltères":      "Rear Delt Fly",
  // Overhead Triceps Extension — rope/cable/dumbbell all → same entry
  "Overhead Rope Triceps Extension":     "Overhead Triceps Extension",
  "Overhead Rope Extension":             "Overhead Triceps Extension",
  "Overhead Cable Triceps Extension":    "Overhead Triceps Extension",
  "Overhead Dumbbell Triceps Extension": "Overhead Triceps Extension",
  "DB Overhead Triceps Extension":       "Overhead Triceps Extension",
  "Dumbbell Overhead Triceps Extension": "Overhead Triceps Extension",
  // Cable Triceps Pushdown — cable / rope / bar variants
  "Rope Triceps Pushdown":               "Cable Triceps Pushdown",
  "Cable Rope Pushdown":                 "Cable Triceps Pushdown",
  "Cable Rope Triceps Pushdown":         "Cable Triceps Pushdown",
  "Bar Triceps Pushdown":                "Cable Triceps Pushdown",
  "Triceps Pushdown (câble corde)":      "Cable Triceps Pushdown",
  "Triceps Rope Pushdown":               "Cable Triceps Pushdown",
  // Band Triceps Pushdown — elastic variants
  "Band Pushdown":                       "Band Triceps Pushdown",
  "Band Pushdown corde":                 "Band Triceps Pushdown",
  "Single-Arm Band Pushdown":            "Band Triceps Pushdown",

  // ── Wave 2 ───────────────────────────────────────────────────

  // Barbell Bench Press base names
  "Bench Press":                         "Barbell Bench Press",

  // Incline press → Incline Barbell Bench Press
  "Incline Bench Press":                 "Incline Barbell Bench Press",
  "Incline Barbell Press":               "Incline Barbell Bench Press",

  // Incline Dumbbell Press variants
  "Dumbbell Incline Press":              "Incline Dumbbell Press",
  "Incline Dumbbell Bench Press":        "Incline Dumbbell Press",

  // Incline Dumbbell Curl (DB + banc incliné)
  "DB Curl appui banc incliné":          "Incline Dumbbell Curl",
  "DB Curl banc incliné":                "Incline Dumbbell Curl",
  "Incline Curl":                        "Incline Dumbbell Curl",

  // Pull-Up variants
  "Weighted Pull-Up":                    "Pull-Up",
  "Traction lestée":                     "Pull-Up",
  "Traction":                            "Pull-Up",
  "Traction élastique prise large":      "Assisted Pull-Up",
  "Band-Assisted Pull-Up":               "Assisted Pull-Up",
  "Traction supination barre de porte":  "Chin-Up",

  // Dumbbell Swing → Kettlebell Swing
  "Dumbbell Swing":                      "Kettlebell Swing",

  // Single-Arm rows → Dumbbell Row
  "Single-Arm DB Row":                   "Dumbbell Row",
  "Single-Arm Dumbbell Row":             "Dumbbell Row",

  // Deadlift base names
  "Deadlift":                            "Barbell Deadlift",
  "Deadlift conventionnel":              "Barbell Deadlift",
  "Sumo Deadlift":                       "Barbell Sumo Deadlift",

  // Dumbbell RDL
  "Dumbbell RDL":                        "Dumbbell Romanian Deadlift",

  // Single-Leg RDL variants
  "DB Single-Leg RDL":                   "Single-Leg RDL",
  "Single-Leg Dumbbell RDL":             "Single-Leg RDL",
  "Single-Leg Romanian Deadlift":        "Single-Leg RDL",

  // Bent-over row variants
  "Dumbbell Bent-Over Row":              "Bent-Over Dumbbell Row",
  "DB Bent-Over Row":                    "Bent-Over Dumbbell Row",
  "Bent Over Row":                       "Bent-Over Row",
  "Barbell Row":                         "Bent-Over Row",

  // Chest-Supported Row
  "Chest-Supported Dumbbell Row":        "Chest-Supported Row",
  "Dumbbell Chest-Supported Row":        "Chest-Supported Row",

  // Pendlay Row
  "DB Pendlay Row":                      "Pendlay Row",
  "Dumbbell Pendlay Row depuis sol":     "Pendlay Row",

  // Curl variants
  "Dumbbell Biceps Curl":                "Dumbbell Curl",
  "Dumbbell Alternate Curl":             "Dumbbell Curl",
  "DB Biceps Curl":                      "Dumbbell Curl",
  "Barbell Biceps Curl":                 "Barbell Curl",

  // Preacher Curl variants
  "Preacher EZ-Bar Curl":                "Preacher Curl",
  "EZ-Bar Preacher Curl":                "Preacher Curl",
  "Dumbbell Preacher Curl":              "Preacher Curl",

  // Hammer Curl
  "Cross-Body Hammer Curl":              "Hammer Curl",

  // Band Biceps Curl
  "Band Rope Curl":                      "Band Biceps Curl",

  // Triceps — Skull Crusher variants
  "Dumbbell Skull Crusher":              "Skull Crusher",
  "EZ Skull Crusher":                    "Skull Crusher",
  "Lying Triceps Extension":             "Skull Crusher",

  // Triceps — Overhead Extension
  "Dumbbell Overhead Extension":         "Overhead Triceps Extension",
  "Overhead DB Extension":               "Overhead Triceps Extension",
  "Cable Overhead Triceps Extension":    "Overhead Triceps Extension",

  // Rear Delt Fly — additional variant
  "Bent-Over Reverse Fly":               "Rear Delt Fly",

  // Barbell Overhead Press variants
  "Overhead Press":                      "Barbell Overhead Press",
  "Standing OHP":                        "Barbell Overhead Press",
  "Standing Barbell OHP":                "Barbell Overhead Press",
  "Standing Barbell Overhead Press":     "Barbell Overhead Press",
  "Standing Overhead Press":             "Barbell Overhead Press",
  "Seated Barbell OHP":                  "Barbell Overhead Press",
  "Seated Barbell Overhead Press":       "Barbell Overhead Press",
  "Seated Barbell Shoulder Press":       "Barbell Overhead Press",

  // Dumbbell Shoulder Press variants
  "Dumbbell Overhead Press":             "Dumbbell Shoulder Press",
  "Dumbbell Overhead Press debout":      "Dumbbell Shoulder Press",
  "DB Shoulder Press debout":            "Dumbbell Shoulder Press",
  "Seated DB Shoulder Press":            "Dumbbell Shoulder Press",
  "Seated Dumbbell Press":               "Dumbbell Shoulder Press",
  "Seated Dumbbell Shoulder Press":      "Dumbbell Shoulder Press",
  "Seated Shoulder Press":               "Dumbbell Shoulder Press",
  "Standing Dumbbell Shoulder Press":    "Dumbbell Shoulder Press",
  "Standing Dumbbell Overhead Press":    "Dumbbell Shoulder Press",
  "Standing DB Press":                   "Dumbbell Shoulder Press",
  "Dumbbell OH Press":                   "Dumbbell Shoulder Press",
  "Dumbbell Single-Arm Press":           "Dumbbell Shoulder Press",
  "Single-Arm Dumbbell Press":           "Dumbbell Shoulder Press",

  // Dumbbell Bench Press variants
  "Dumbbell Chest Press":                "Dumbbell Bench Press",

  // Dumbbell Fly variants
  "Dumbbell Floor Fly":                  "Dumbbell Fly",
  "DB Floor Fly":                        "Dumbbell Fly",

  // Close-Grip (hyphen normalisation)
  "Close Grip Bench Press":              "Close-Grip Bench Press",

  // Decline
  "Decline Bench Press":                 "Decline Barbell Bench Press",

  // Dips
  "Weighted Dips":                       "Dips",
  "Weighted Dip":                        "Dips",

  // Cable / Band variants
  "Band Pallof Press":                   "Pallof Press",
  "Band Pull-Through":                   "Cable Pull-Through",
  "Cable Chest Fly":                     "Cable Fly",
  "Cable Glute Kickback":                "Cable Kickback",
  "Band Kickback":                       "Cable Kickback",
  "Banded Glute Kickback":               "Cable Kickback",
  "Resistance Band Glute Kickback":      "Cable Kickback",
  "Straight-Arm Band Pulldown":          "Straight-Arm Pulldown",
  "Single-Arm Band Pulldown":            "Band Pulldown",
  "Single-Arm Lat Pulldown":             "Lat Pulldown",
  "Band Seated Row":                     "Band Row",
  "Resistance Band Lateral Raise":       "Band Lateral Raise",
  "Band Low Fly":                        "Band Chest Fly",
  "Resistance Band Clam / Side-Lying Hip Abduction": "Clamshell",

  // Calf raises
  "Single Leg Calf Raise":               "Single-Leg Calf Raise",
  "Calf Raise assis":                    "Seated Calf Raise",
  "Calf Raise debout":                   "Standing Calf Raise",
  "Standing Dumbbell Calf Raise":        "Standing Calf Raise",

  // Step-Ups
  "Step-Ups lestés":                     "Step-Up",
  "Box Step-Up rapide":                  "Step-Up",

  // Hip movements
  "Hip Abduction machine":               "Seated Hip Abduction",
  "Single Leg Hip Thrust":               "Hip Thrust",
  "Single-Leg Hip Thrust":               "Hip Thrust",

  // Push Press
  "DB Push Press":                       "Push Press",
  "KB Push Press":                       "Push Press",
  "Dumbbell Push Press":                 "Push Press",

  // Thruster
  "Dumbbell Thruster":                   "Thruster",
  "KB Thrusters":                        "Thruster",

  // KB / DB misc
  "KB Front Squat":                      "Front Squat",
  "DB Farmer's Carry":                   "Farmer's Carry",
  "DB Upright Row":                      "KB Upright Row",
  "DB Lateral Raise":                    "Dumbbell Lateral Raise",
  "Dumbbell Bulgarian Split Squat":      "Bulgarian Split Squat",
  "Heel-Elevated Goblet Squat":          "Goblet Squat",

  // Seated Cable Row
  "Cable Seated Row with V bar":         "Seated Cable Row",

  // Nordic / core
  "Nordic Hamstring Curl":               "Nordic Curl",
  "Hollow Hold":                         "Hollow Body Hold",
  "Front Plank":                         "Plank",
  "Bear Plank":                          "Bear Plank Hold",

  // Airbike
  "Airbike":                             "Air Bike",

  // Typos / spelling variants
  "Bird Dog":                            "Bird-Dog",
  "Burpees":                             "Burpee",
  "Jumping Jacks":                       "Jumping Jack",

  // Russian Twist
  "Dumbbell Russian Twists":             "Russian Twist",
  "KB Russian Twists":                   "Russian Twist",

  // ── Wave 3 ───────────────────────────────────────────────────

  // Leg Curl safety net (JSON fixes cover most; this catches stripped variants)
  "Leg Curl":                            "Lying Leg Curl",

  // Hammer Curl
  "Dumbbell Hammer Curl":                "Hammer Curl",
  "DB Hammer Curl":                      "Hammer Curl",

  // Arnold Press
  "Dumbbell Arnold Press":               "Arnold Press",

  // Face Pull
  "Face Pull":                           "Cable Face Pull",
  "Rope Face Pull":                      "Cable Face Pull",

  // Rows
  "Seated Row":                          "Seated Cable Row",
  "Cable Row":                           "Seated Cable Row",
  "Bent-Over DB Row":                    "Bent-Over Dumbbell Row",
  "Dumbbell Bent-Over Row pronation":    "Bent-Over Dumbbell Row",
  "DB Row 1 bras":                       "Dumbbell Row",
  "KB Row":                              "Dumbbell Row",
  "Single-Arm DB Row explosif":          "Dumbbell Row",
  "Single-Arm Dumbbell Row explosif":    "Dumbbell Row",

  // Cable / Lat Pulldown
  "Cable Pulldown":                      "Lat Pulldown",
  "Close-Grip Pulldown":                 "Lat Pulldown",

  // Upright Row
  "Upright Row":                         "KB Upright Row",

  // Floor Press (without equipment → Dumbbell)
  "Floor Press":                         "Dumbbell Floor Press",

  // Chin-Up variants
  "Weighted Chin-Up":                    "Chin-Up",
  "Traction supination":                 "Chin-Up",

  // Pull-Up / Assisted
  "Traction élastique":                  "Assisted Pull-Up",
  "Traction barre de porte":             "Pull-Up",
  "Traction barre porte":                "Pull-Up",
  "Traction barre de porte / Band-assistée": "Assisted Pull-Up",
  "Traction barre de porte / Traction band-assistée": "Assisted Pull-Up",

  // Triceps
  "Overhead Extension":                  "Overhead Triceps Extension",
  "Rope Pushdown":                       "Cable Triceps Pushdown",
  "Lying Triceps Extension EZ":          "Skull Crusher",

  // Skull Crusher / Bench pauses
  "Bench Press pauses":                  "Barbell Bench Press",

  // Incline variants (additional word orders)
  "Incline BB Bench":                    "Incline Barbell Bench Press",
  "Incline Barbell Bench":               "Incline Barbell Bench Press",
  "Incline Dumbbell Bench":              "Incline Dumbbell Press",
  "DB Incline Press":                    "Incline Dumbbell Press",

  // Dumbbell Shoulder Press (additional)
  "DB OHP":                              "Dumbbell Shoulder Press",

  // Flat Press variants
  "DB Flat Press":                       "Dumbbell Bench Press",

  // Walking Lunge
  "DB Walking Lunge":                    "Walking Lunge",
  "Dumbbell Walking Lunge":              "Walking Lunge",

  // Goblet Squat
  "Front Goblet Squat":                  "Goblet Squat",

  // Concentration Curl
  "Dumbbell Concentration Curl":         "Concentration Curl",

  // Cable Biceps Curl
  "Cable Rope Curl":                     "Cable Biceps Curl",

  // Bulgarian Split Squat
  "BSS lesté":                           "Bulgarian Split Squat",

  // Band / Cable Kickback
  "Band Glute Kickback":                 "Cable Kickback",

  // Leg / Slider extensions
  "Single-Leg Extension":                "Leg Extension",
  "Single-Leg Slider Leg Curl":          "Slider Leg Curl",

  // Bird-Dog with weight
  "Bird Dog avec haltère léger":         "Bird-Dog",

  // Pendlay Row
  "Dumbbell Pendlay Row":                "Pendlay Row",

  // High Knees
  "High Knees":                          "High Knee Skips",

  // Cardio
  "Récupération active vélo":            "Vélo",
  "airbike":                             "Air Bike",

  // Fly / Cable variants
  "Resistance Band Fly":                 "Band Chest Fly",
  "Resistance Band Crossover":           "Band Chest Fly",
  "Cable Crossover":                     "Cable Fly",
  "Post-fatigue Fly":                    "Cable Fly",
  "DB Crossover Fly":                    "Dumbbell Fly",
  "Machine Rear Delt Fly":               "Rear Delt Fly",

  // Straight-Arm Pulldown (band variant)
  "Band Straight-Arm Pulldown":          "Straight-Arm Pulldown",

  // Thruster
  "Dumbbell Squat to Press":             "Thruster",

  // Calf raise variants
  "Calf Raise assis haltères":           "Seated Calf Raise",
  "Calf Raise unilatéral chargé haltère": "Single-Leg Calf Raise",

  // Dips
  "Band-Assisted Dips entre 2 chaises":  "Bench Dips",

  // Hanging Leg Raise (Captain's Chair variant)
  "Captain's Chair Leg Raise":           "Hanging Leg Raise",

  // Pallof Press band variants
  "Band Pallof Press avec rotation":     "Pallof Press",
  "Band Pallof Press statique 5''":      "Pallof Press",

  // Calf Raise (with dumbbell qualifier)
  "Calf Raise debout haltère":           "Standing Calf Raise",
  "Smith Calf Raise":                    "Standing Calf Raise",

  // DB Floor Press (French naming)
  "DB Bench Press au sol":               "Dumbbell Floor Press",

  // DB Front Squat (same movement as barbell variant)
  "DB Front Squat":                      "Front Squat",

  // Résistance Band Kickback (accent typo in JSON)
  "Résistance Band Kickback":            "Cable Kickback",

  // ── Wave 4 (S23-2 — unresolved.json triage, vraies variantes) ──────────────

  "Dumbbell Shoulder Press debout":      "Dumbbell Shoulder Press",
  "Push-Up Diamond":                     "Diamond Push-Up",
  "Jump Box":                            "Box Jump",
  "Jump Squat":                          "Squat Jump",
  "KB Swing":                            "Kettlebell Swing",
  "KB Swings":                           "Kettlebell Swing",
  "Hack Squat machine":                  "Hack Squat",
  "Wall Ball Shots":                     "Wall Ball",
  "Wall Ball Shot":                      "Wall Ball",
  "Hanging Knee Raise":                  "Hanging Leg Raise",
  "Air Squats":                          "Bodyweight Squat",
  "Air Squat":                           "Bodyweight Squat",

  // Push-Up / Plank / Dead Bug — qualificatif de méthode, même mouvement de base
  "Push-Up progressif":                  "Push-Up",
  "Plank classique":                     "Plank",
  "Dead Bug avec haltère léger":         "Dead Bug",
  "Dead Bug avec":                       "Dead Bug",
  "Dead Bug avec extension lente":       "Dead Bug",
  "Glute Bridge avec resistance band":   "Glute Bridge",
  "Clamshell avec resistance band":      "Clamshell",
  "Mountain Climbers en intervalles équivalents": "Mountain Climbers",

  // Pull-Up / Chin-Up — équipement identique (barre de porte / élastique), même
  // mouvement (cf. alias "Traction barre de porte"/"Traction élastique" existants)
  "Pull-Up barre porte":                 "Pull-Up",
  "Pull-Up élastique":                   "Assisted Pull-Up",
  "Chin-Up barre porte":                 "Chin-Up",
  "Superman":                            "Superman Hold",
  "RKC Plank":                           "Plank",

  // Biceps / Triceps — même équipement, nom raccourci ou réordonné
  "Cable Curl":                          "Cable Biceps Curl",
  "Preacher":                            "Preacher Curl",
  "Skull Crusher EZ":                    "Skull Crusher",
  "Close Grip Bench":                    "Close-Grip Bench Press",
  "Close-Grip Bench":                    "Close-Grip Bench Press",
  "CGBP":                                "Close-Grip Bench Press",
  "Pec Deck Machine":                    "Pec Deck",
  "Overhead Rope Ext":                   "Overhead Triceps Extension",
  "Incline Fly":                         "Incline Dumbbell Fly",
  "Flat Bench Press":                    "Barbell Bench Press",
  "Flat Bench":                          "Barbell Bench Press",

  // Goblet Squat — variantes positionnelles, même équipement
  "Goblet Squat front-rack":             "Goblet Squat",
  "Goblet Squat haute position":         "Goblet Squat",

  // Hip Thrust / Glute Bridge — variantes positionnelles/équipement identique
  "Hip Thrust haltère sur bassin":       "Hip Thrust",
  "Hip Thrust barbell":                  "Hip Thrust",
  "Hip Thrust barre":                    "Hip Thrust",
  "Glute Bridge barre":                  "Glute Bridge",
  "Hip Abduction":                       "Seated Hip Abduction",

  // Dumbbell Row / Floor Press / Curl — variantes de prise/banc, même équipement
  "Dumbbell Row prise neutre":           "Dumbbell Row",
  "Dumbbell Row sur banc incliné":       "Dumbbell Row",
  "Dumbbell Row banc incliné":           "Dumbbell Row",
  "Dumbbell Row poitrine sur banc incliné": "Dumbbell Row",
  "Dumbbell Curl sur banc incliné":      "Incline Dumbbell Curl",
  "Dumbbell Floor Press incliné":        "Dumbbell Floor Press",
  "Dumbbell Floor Press surélevé":       "Dumbbell Floor Press",
  "Incline DB Floor Press":              "Dumbbell Floor Press",
  "Dumbbell Bench Press au sol":         "Dumbbell Floor Press",
  "Lat Pulldown prise neutre":           "Lat Pulldown",
  "Cable Fly haut":                      "Cable Fly",
  "Band Pulldown élastique":             "Band Pulldown",
  "Band Row assis":                      "Band Row",

  // DB abréviation — mouvements déjà couverts en toutes lettres
  "DB Thruster":                         "Thruster",
  "DB Walking Lunges":                   "Walking Lunge",
  "Walking Lunge PDC":                   "Walking Lunge",

  // Block Pull — vrai exercice (Lombaires), "finisher" est juste son rôle dans
  // la séance, pas une annotation de méthode à filtrer.
  "Block Pull finisher":                 "Block Pull",

  // Goblet Squat — équipement interchangeable (haltère/kettlebell tenu identique
  // devant la poitrine), mouvement et vidéo inchangés
  "KB Goblet Squat":                     "Goblet Squat",
  "DB Overhead Press":                   "Dumbbell Shoulder Press",

  // ── Wave 5 (S23-2 suite — 13 exercices nouvellement créés + triage manuel) ──

  "Dips entre 2 chaises":                "Bench Dips",
  "Lateral Raise":                       "Dumbbell Lateral Raise",
  "Hammer Curl corde":                   "Cable Hammer Curl",
  "DB RDL":                              "Dumbbell Romanian Deadlift",
  "RDL":                                 "Romanian Deadlift",
  "Landmine RDL":                        "Romanian Deadlift",
  "Push-Up Renegade Row":                "Renegade Row",
  "Squat":                               "Back Squat",
  "Bench":                               "Barbell Bench Press",
  "Incline":                             "Incline Barbell Bench Press",
  "Goblet Squat to Press":               "Thruster",
  "Plank Shoulder Taps":                 "Plank",
  "Seated DB Press":                     "Dumbbell Shoulder Press",
  "Standing Push Press":                 "Push Press",
  "Standing DB Curl":                    "Dumbbell Curl",
  "Incline DB":                          "Incline Dumbbell Curl",
  "French Press":                        "Overhead Triceps Extension",
  "DB French Press léger":               "Overhead Triceps Extension",
  "DB French léger":                     "Overhead Triceps Extension",
  "Lever Lateral Raise":                 "Machine Lateral Raise",
  "Lever Seated Shoulder Press":         "Machine Shoulder Press",
  "Pec Deck inversé":                    "Rear Delt Fly",
  "Rear Delt Fly machine":               "Rear Delt Fly",
  "Low Row":                             "Seated Cable Row",
  "Standing Leg Curl":                   "Lying Leg Curl",
  "Squat Clean":                         "Power Clean",
  "DB Clean":                            "Dumbbell Clean",
  "KB Clean":                            "Dumbbell Clean",
  "Dumbbell Hang Clean":                 "Dumbbell Clean",
  "DB Deadlift":                         "Dumbbell Deadlift",
  "DB Sumo Deadlift":                    "Dumbbell Sumo Deadlift",
  "Donkey Calf Raise":                   "Standing Calf Raise",
  "Leg Press Calf Raise":                "Standing Calf Raise",
  "Knee Raise":                          "Hanging Leg Raise",
  "Leg Raise Hip Lift":                  "Lying Leg Raise",
  "DB Fly":                              "Dumbbell Fly",
  "Jump Rope double":                    "Jump Rope",
  "Double-Under OU High Knees rapides":  "High Knee Skips",
  "Floor Slap":                          "Burpee",
  "Burpees rapides":                     "Burpee",
  "Burpee Broad Jump":                   "Broad Jump",
  "Reverse":                             "Reverse Lunge",
  "Row":                                 "Bent-Over Row",
  "KB Bent-Over Row":                    "Bent-Over Row",
  "Banded Hip Abduction":                "Band Hip Abduction",
  "Resistance Band Hip Abduction":       "Band Hip Abduction",
  "External Rotation":                   "Standing Shoulder External Rotation",
  // "Chin-Up élastique" : même traitement que "Pull-Up élastique" (ligne 431) —
  // assisté élastique = Assisted Pull-Up, quel que soit le grip d'origine.
  "Chin-Up élastique":                   "Assisted Pull-Up",

  // "Step-Ups" (pluriel) — nom réel est "Step-Up" (singulier), cf. pattern
  // Burpees→Burpee / Jumping Jacks→Jumping Jack déjà en place.
  "Step-Ups":                            "Step-Up",

  // Parenthèse porteuse d'équipement — cas unique, PAS de règle générale (le
  // strip parenthétique générique jette normalement tout le contenu entre
  // parenthèses ; ici il distingue le vrai exercice). Clé = chaîne brute
  // complète, matchée avant tout stripping (step 2).
  "Biceps Curl (EZ-bar)":                "EZ Bar Curl",
  "Biceps Curl (haltères)":              "Dumbbell Curl",
  "Biceps Curl":                         "Dumbbell Curl",

  // ── Échauffement ─────────────────────────────────────────────
  // Noms d'items d'échauffement sans équivalent en table : le programme décrit le
  // MATÉRIEL ou l'ORIENTATION du mouvement là où la table nomme le mouvement seul.
  // Écarts de contenu réels, pas des variantes de graphie — d'où l'alias plutôt
  // qu'un assouplissement du matching.
  "Resistance Band Horizontal Pallof Press": "Pallof Press",
  "Resistance Band Clam":                 "Clamshell",
  "Kettlebell One Legged Deadlift":       "Single-Leg RDL",
  // Deux items DISTINCTS dans les programmes (jamais un item combiné) : rotation
  // interne et rotation externe ont chacune leur vidéo.
  "Cable Shoulder 90 Degree Internal":    "Cable Shoulder Internal Rotation 90°",
  "Cable Shoulder 90 Degree External":    "Cable Shoulder External Rotation 90°",
}

// "A + B" (superset/bi-set) et "A → B" (avantage mécanique / contrast training)
// sont tous deux des ENCHAÎNEMENTS de deux mouvements distincts — on les splitte
// pareil. On ignore les occurrences à l'intérieur de parenthèses : celles-ci sont
// des libellés de technique ("EMPILAGE : Cluster + Burn-Out"), pas des exercices.
function splitTopLevelCombo(s: string): string[] {
  const parts: string[] = []
  let depth = 0
  let current = ""
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]
    if (ch === "(") depth++
    else if (ch === ")") depth = Math.max(0, depth - 1)
    if (depth === 0 && (s.startsWith(" + ", i) || s.startsWith(" → ", i))) {
      parts.push(current)
      current = ""
      i += 2 // + the for-loop's own i++ => skip the 3-char separator entirely
      continue
    }
    current += ch
  }
  parts.push(current)
  return parts
}

// "A / B" = alternative selon l'équipement disponible ("l'un OU l'autre"), pas un
// enchaînement — on ne garde que le premier choix pour la charge/les instructions.
function stripFirstAlternative(s: string): string {
  const idx = s.indexOf(" / ")
  return idx === -1 ? s : s.slice(0, idx).trim()
}

// Qualificatifs collés en fin de nom (sans parenthèses ni tiret pour les isoler) :
// "Incline Dumbbell Press 30°", "Dumbbell Row 1 bras", "Hip Thrust haltère",
// "Pull-Up chargé", "Goblet Squat lourd", "Assisted..." (déjà couvert par alias).
// Strippés un par un (boucle) pour gérer les qualificatifs composés
// ("Standing Calf Raise lesté unilatéral").
const TRAILING_DEGREE_RE = /\s*\d+°$/
const TRAILING_ARM_COUNT_RE = /\s+\d+[\s-]?bras$/i
const TRAILING_QUALIFIER_RE =
  /\s+(haltères?|lest[ée]e?s?|charg[ée]e?s?|lourde?s?|assist[ée]e?s?|unilat[ée]rale?s?|pauses?|tempo|avec pause|barre|PDC|strict|explosif|rapide|dynamique|avec rotation|pieds banc|sac à dos|prise large|iso 2''\s*fin|pied haut\/large|AMRAP test|AMRAP|\d+\s*cm)$/i
// Reps/count en suffixe ("Burpee 8", "Box Jump 10", "Hip Thrust 2") — miroir du
// préfixe déjà géré par stripLeadingRepsNumber. Ignoré si précédé de "×"/"x"
// (multiplicateur, cf TRAILING_MULTIPLIER_RE) pour ne pas se marcher dessus.
const TRAILING_REPS_RE = /\s+\d+(?:[.,]\d+)?$/
// "× N"/"x N" en suffixe ("Push-Up × 60", "Tabata × 2") — reps ou rounds, jamais
// une distance/tempo mi-chaîne (celles-ci ne sont jamais en toute fin de chaîne).
const TRAILING_MULTIPLIER_RE = /\s*[×x]\s*\d+(?:[.,]\d+)?$/i
// "N/unité" en suffixe ("Step-Ups 10/jambe" → reps par côté, pas un choix
// d'équipement comme le "A / B" avec espaces de stripFirstAlternative).
const TRAILING_PER_UNIT_RE = /\s+\d+\/\S+$/

function stripTrailingQualifiers(s: string): string {
  let out = s
  for (let i = 0; i < 5; i++) {
    const before = out
    out = out
      .replace(TRAILING_DEGREE_RE, "")
      .replace(TRAILING_ARM_COUNT_RE, "")
      .replace(TRAILING_QUALIFIER_RE, "")
      .replace(TRAILING_MULTIPLIER_RE, "")
      .replace(TRAILING_PER_UNIT_RE, "")
      .trim()
    const repsMatch = TRAILING_REPS_RE.exec(out)
    if (repsMatch && !/[×x]\s*$/i.test(out.slice(0, repsMatch.index))) {
      out = out.slice(0, repsMatch.index).trim()
    }
    if (out === before) break
  }
  return out
}

// Tiret/point médian utilisés comme séparateur "Exercice — annotation de méthode"
// (l'exercice est AVANT, l'annotation après — cf. step 3 plus bas). Si l'un
// d'eux précède un ":", ce ":" est à l'intérieur de l'annotation de méthode, pas
// un séparateur "descripteur : exercice" — cf. extractAfterColon.
const METHOD_SEPARATOR_RE = /[—–·]/

// Libellé "descripteur : Exercice" — "Bi-set biceps : Barbell Curl", "Étape 2 :
// Push-Up", "Density : Bench Press", "Triceps : JM Press", "EMOM 12 — Min 1 :
// Goblet Squat", "Station R3 : Burpees". Le vrai exercice est TOUJOURS après le
// DERNIER ":" — quel que soit le descripteur devant (pas de liste fermée : un
// bi-set, une étape numérotée, un groupe musculaire, un protocole de densité...
// sont tous des préfixes valables).
// Deux gardes, toutes deux nécessaires pour ne considérer QUE le ":" qui sépare
// vraiment "descripteur : exercice" (pas un ":" noyé dans une parenthèse de
// méthode, ni un ":" précédé d'un tiret/point médian) :
//  1. Un ":" à l'intérieur d'une parenthèse ne compte pas — "Bench Press
//     (EMPILAGE DOUBLE : Pyramide 7-sets + Cluster...)" a l'exercice AVANT la
//     parenthèse ; le ":" y est un détail de méthode que step 3 supprimera de
//     toute façon avec toute la parenthèse. On ne regarde donc que la portion
//     de chaîne AVANT la première "(" pour savoir si la règle s'applique.
//  2. Si un "—"/"–"/"·" précède ce ":", ce n'est pas non plus ce motif mais
//     l'inverse — "Deadlift conventionnel — EMPILAGE TRIPLE : Cluster" a
//     l'exercice AVANT le tiret et une annotation de méthode (qui contient
//     elle-même un ":") après ; prendre le texte après ce ":" donnerait
//     "Cluster" au lieu de "Deadlift conventionnel". Dans les deux cas on
//     laisse la chaîne intacte pour que le strip de fin de chaîne (step 3)
//     coupe tout depuis la parenthèse/le tiret/le point médian.
function extractAfterColon(s: string): string {
  const parenIdx = s.indexOf("(")
  const searchRegion = parenIdx === -1 ? s : s.slice(0, parenIdx)
  if (!searchRegion.includes(":")) return s
  const colonIdx = searchRegion.lastIndexOf(":")
  if (METHOD_SEPARATOR_RE.test(searchRegion.slice(0, colonIdx))) return s
  const rest = s.slice(colonIdx + 1).trim()
  return rest || s
}

// Libellé WOD par tiret/point médian, SANS ":" — préfixe fermé (EMOM/AMRAP/
// Tabata/Finisher/Station, ou un schéma de reps "21-15-9"/"50-40-30-20-10") :
// le vrai exercice est le dernier segment après le séparateur.
// Contrairement au ":", on ne généralise pas ces séparateurs à un préfixe
// ouvert — "Hack Squat machine — Myo-Reps set 4" ou "Push Press · EMPILAGE :
// ..." ont la sémantique inverse (exercice AVANT le séparateur, annotation
// après), gérée par le strip de fin de chaîne (step 3).
const WOD_PREFIX_RE = /^(EMOM|AMRAP|Tabata|Finisher|Station)\b/i
const REP_SCHEME_RE = /^\d+(-\d+)+$/
// `(.*)` (pas `(.+)`) : avec `(.+)`, "AMRAP 12 min" sans rien après force le
// moteur regex à backtracker — le groupe optionnel digits+min lâche "min" pour
// satisfaire le `+`, et "min" se retrouve traité comme l'exercice attaché
// ("AMRAP 12 min" → "Min" !). `(.*)` accepte un reste vide, laissant le garde
// ci-dessous (rest non vide + commence par une lettre) filtrer correctement.
const WOD_DIRECT_ATTACH_RE = /^(EMOM|AMRAP|Tabata|Finisher|Station)\s+(?:\d+(?:[.,]\d+)?\s*(?:min)?\s*)?(.*)$/i

function capitalizeFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function extractAfterWodPrefix(s: string): string {
  const afterColon = extractAfterColon(s)
  if (afterColon !== s) return afterColon

  if (/[—–·]/.test(s)) {
    const segments = s.split(/\s*[—–·]\s*/)
    const prefix = segments[0].trim()
    if (segments.length >= 2 && (WOD_PREFIX_RE.test(prefix) || REP_SCHEME_RE.test(prefix))) {
      return segments[segments.length - 1].trim()
    }
    return s
  }
  const m = WOD_DIRECT_ATTACH_RE.exec(s)
  if (m) {
    const rest = m[2].trim()
    // Guard: only accept if a real word follows (not just a parenthetical/format
    // note like "EMOM 12 min (alternance)", or nothing at all like "AMRAP 12
    // min" bare — neither has an attached exercise).
    if (rest && /^[A-Za-zÀ-ÖØ-öø-ÿ]/.test(rest)) return capitalizeFirst(rest)
  }
  return s
}

// Préfixe numérique de reps/distance en tête ("10 Burpees" → "Burpees", "200m
// course" → "Course"). Ne matche qu'un préfixe EN TÊTE de chaîne — les notations
// tempo/unité mi-chaîne ("30''/30''", "km/h") ne sont jamais touchées.
const LEADING_REPS_RE = /^\d+(?:[.,]\d+)?\s*(?:''|'|km\/h|km|m|min|sec|x|×)?\s+/i

function stripLeadingRepsNumber(s: string): string {
  const m = LEADING_REPS_RE.exec(s)
  if (!m) return s
  const rest = s.slice(m[0].length).trim()
  if (!rest || !/^[A-Za-zÀ-ÖØ-öø-ÿ]/.test(rest)) return s
  return capitalizeFirst(rest)
}

// Fragments de méthode/technique issus du split d'un combiné sur "+"/"→"
// (EMPILAGE, Cluster, Drop Set, Contrast, Bi-set...) — ce ne sont jamais des
// exercices, juste des annotations d'intensification collées dans le même champ
// "name" ("DB Lateral Raise · EMPILAGE TRIPLE : Drop → Cluster → Myo" splitte en
// 3 parts dont 2 sont du bruit). Filtrés du combiné plutôt que mappés à un faux
// exercice — cf. EXERCISE_ALIAS_MAP pour les vraies variantes d'exercice.
// N.B. "Block Pull" est un VRAI exercice de la table (`exercises`, catégorie
// Lombaires) — volontairement absent de cette liste ; "Block Pull finisher" est
// résolu via EXERCISE_ALIAS_MAP, pas filtré comme bruit.
const METHOD_NOISE_BASE_RE =
  /^(Cluster|Drop\s*Set|Drop|Myo-Reps|Myo|Stato-Dyn(?:amique)?|Stato|Rest-Pause|Excentrique|Burn-Out|Iso-Dyn|Contrast|Pyramide|Density|Bi-set|Tri-set)(\s+(sommet|final(e)?|finisher|court|long|accentué|dégressif|barbell|\d+%|\d+''?))*$/i
const METHOD_NOISE_EXACT = new Set([
  "Stations", "Final", "Progression", "Léger", "Lourd", "Bande",
  "Round 1", "Round 2", "Round 3", "Round 4", "5",
])

function isMethodNoiseFragment(normalized: string): boolean {
  return METHOD_NOISE_EXACT.has(normalized) || METHOD_NOISE_BASE_RE.test(normalized)
}

// Cardio "maison" chiffré/annoté ("Course 200m", "Fractionné 200m tapis", "Rameur
// 500m", "Airbike 30''/30''", "Tapis Zone 2", "course/marche rapide"...) — mappé
// vers l'entrée générique existante correspondante, en dernier recours (seulement
// si rien d'autre n'a résolu). "Course sur place" est une entrée RÉELLE distincte
// (échauffement) : jamais redirigée, sous peine de régression.
const CARDIO_EXACT_PASSTHROUGH = new Set(["Course sur place", "Squat Air Bike"])
// Note : `\b` (word boundary) est ASCII-only en JS — "Fractionné" suivi d'un
// espace ne déclenche PAS `\b` car "é" n'est pas un "word char" pour la regex,
// donc ni "é" ni l'espace qui suit ne comptent comme limite. On utilise donc un
// lookahead explicite (fin de chaîne ou caractère non-lettre, accents inclus).
const WORD_END = "(?![A-Za-zÀ-ÖØ-öø-ÿ])"
const CARDIO_PREFIX_RULES: Array<[RegExp, string]> = [
  [new RegExp(`^(Course|Fractionn[ée]|Tapis|Marche(\\s+tapis)?|course\\/marche)${WORD_END}`, "i"), "Course / Tapis de course"],
  [new RegExp(`^Rameur${WORD_END}`, "i"), "Rameur"],
  [new RegExp(`^(Air\\s?bike)${WORD_END}`, "i"), "Air Bike"],
  [new RegExp(`^V[ée]lo${WORD_END}`, "i"), "Vélo"],
  [new RegExp(`^Corde(\\s+[àa]\\s+sauter)?${WORD_END}|^Corde\\s*[×x]`, "i"), "Corde à sauter"],
  [new RegExp(`^Ski\\s?Erg${WORD_END}`, "i"), "Ski Erg"],
]

function resolveCardioPrefix(s: string): string | null {
  if (CARDIO_EXACT_PASSTHROUGH.has(s)) return null
  for (const [re, target] of CARDIO_PREFIX_RULES) {
    if (re.test(s)) return target
  }
  return null
}

// ── Formats de travail / bruit résiduel — décision produit S23-2 ───────────
// Ces libellés ("AMRAP 12 min", "EMOM 15 min", "DB Complex"...) occupent un
// slot d'exercice dans le JSON (order/sets/reps/load/tempo) mais ne DÉSIGNENT
// pas un exercice démontrable : les mouvements réels sont listés dans `reps`
// ou `notes`. Contrairement aux autres cas de ce fichier, on ne les mappe PAS
// vers un exercices.name — on les EXPOSE pour que check-exercise-ids.js les
// reconnaisse et les exempte de l'obligation d'`exercise_id`, plutôt que
// d'exiger un mapping qui n'a pas de sens.
//
// "DB Complex"/"KB Complex" ne contiennent aucun mot-clé de format — la
// détection ne peut donc pas reposer sur un seul motif générique côté script ;
// c'est pourquoi cette liste est un ALLOWLIST EXPLICITE de noms exacts (après
// normalisation), pas une regex ouverte.
const WORK_FORMAT_PREFIX_RE = /^(AMRAP|EMOM|Tabata|Chipper|Circuit)\b/i
const WORK_FORMAT_CANONICAL: Record<string, string> = {
  amrap: "AMRAP", emom: "EMOM", tabata: "Tabata", chipper: "Chipper", circuit: "Circuit",
}

// Réduit "AMRAP 12 min", "EMOM 15 min (alternance)", "Tabata × 2"... au mot-clé
// nu, UNIQUEMENT si aucun exercice réel n'a pu être extrait avant (sinon la
// récursion de extractAfterWodPrefix aurait déjà résolu et court-circuité ce
// point). Ne couvre PAS "Circuit avancé 4 rounds : Thruster" (a un ":", déjà
// résolu à "Thruster" en amont) ni "Circuit 30''/30'' × 5 tours — 4 stations"
// (a un contenu propre après strip, cf WORK_FORMAT_NAMES littéral ci-dessous).
function collapseWorkFormatLabel(s: string): string | null {
  const m = WORK_FORMAT_PREFIX_RE.exec(s)
  return m ? WORK_FORMAT_CANONICAL[m[1].toLowerCase()] : null
}

/**
 * Canonical labels that are NOT exercises — exported so check-exercise-ids.js
 * (or any other consumer) can treat a `normalizeExerciseName()` output equal
 * to one of these as "exempt from requiring an exercise_id", instead of
 * failing the build. See the comment above `collapseWorkFormatLabel` for why
 * this can't be a pure name-pattern check.
 */
export const NON_EXERCISE_LABELS = new Set([
  // Formats de travail (mouvements réels dans `reps`/`notes`, pas dans `name`)
  "AMRAP", "EMOM", "Tabata", "Chipper", "Circuit",
  "21-15-9", "Stations", "Stations rotatives", "Rotative R1-R7", "station × 7 rounds",
  "Circuit Pyramide montante 3-6-9-12-15", "Circuit 30''/30'' × 5 tours",
  "DB Complex", "KB Complex", "Dumbbell Complex", "DB Complex léger", "DB Complex Heavy",
  // Bruit résiduel — fragment sans exercice identifiable derrière
  "bande", "lest", "lest sac à dos", "Alternée", "léger", "8×", "200m", "À 8 km/h",
  "Final", "progression", "Biceps", "Triceps",
])

/**
 * Resolves a JSON programme exercise name to the canonical name used in the
 * `exercises` table.  Resolution order:
 *  1. Combined exercises ("A + B" / "A → B") — each part normalised independently.
 *  1b. WOD-format label ("EMOM 12 — Min 1 : X", "Station R3 : X") → extract the
 *      real exercise (last segment / word after the prefix), then re-run the
 *      full pipeline on it.
 *  1c. Leading reps/distance prefix ("10 Burpees" → "Burpees") → same re-run.
 *  2. Exact lookup in EXERCISE_ALIAS_MAP (before any stripping — some aliases are
 *     keyed on the full "A / B" phrase, e.g. equipment-note oddities).
 *  3. Strip parenthetical / dash suffixes ("Back Squat (Cluster)" → "Back Squat"),
 *     then alias retry.
 *  4. "A / B" alternative → keep the first equipment choice only, then alias retry.
 *  5. Strip generic trailing qualifiers ("30°", "1 bras", "haltère", "chargé",
 *     "lourd", "assisté", "unilatéral"...), then alias retry.
 *  Returns the best resolved name at whichever step matched (or the last
 *  stripped candidate as a best-effort fallback).
 */
export function normalizeExerciseName(rawName: string): string {
  // 1. Combined exercise: split at the top level only, normalise each part, drop
  // pure method/technique fragments (EMPILAGE, Cluster, Drop Set...) AND residual
  // non-exercise labels (NON_EXERCISE_LABELS — "bande", "léger", "Alternée",
  // "Biceps"...) so a mixed combo like "Hip Thrust + bande" still resolves its
  // real half ("Hip Thrust") instead of failing the whole entry. Only the
  // residual-noise half of NON_EXERCISE_LABELS applies here — the work-format
  // half (AMRAP/EMOM/DB Complex...) only ever appears as a WHOLE standalone
  // name, never mixed into a combo with a real exercise, so dropping is safe.
  const comboParts = splitTopLevelCombo(rawName)
  if (comboParts.length > 1) {
    const normalizedParts = comboParts.map(part => normalizeExerciseName(part.trim()))
    const realParts = normalizedParts.filter(part => !isMethodNoiseFragment(part) && !NON_EXERCISE_LABELS.has(part))
    return (realParts.length > 0 ? realParts : normalizedParts).join(" + ")
  }

  // 1b. WOD-format label — extract the real exercise, then re-run the full pipeline
  const wodExtracted = extractAfterWodPrefix(rawName)
  if (wodExtracted !== rawName) return normalizeExerciseName(wodExtracted)

  // 1c. Leading reps/distance prefix — same re-run
  const numberStripped = stripLeadingRepsNumber(rawName)
  if (numberStripped !== rawName) return normalizeExerciseName(numberStripped)

  // 2. Exact alias match
  if (EXERCISE_ALIAS_MAP[rawName]) return EXERCISE_ALIAS_MAP[rawName]

  // 3. Strip trailing parenthetical and dash/dot-style suffixes, then retry
  let candidate = rawName
    .split("(")[0]
    .replace(/\s+[—–·-].*$/, "")
    .trim()
  if (candidate !== rawName && EXERCISE_ALIAS_MAP[candidate]) return EXERCISE_ALIAS_MAP[candidate]

  // 4. "/" alternative — keep first equipment choice only, then retry
  const altResolved = stripFirstAlternative(candidate)
  if (altResolved !== candidate) {
    candidate = altResolved
    if (EXERCISE_ALIAS_MAP[candidate]) return EXERCISE_ALIAS_MAP[candidate]
  }

  // 5. Generic trailing-qualifier strip, then retry
  const qualifierStripped = stripTrailingQualifiers(candidate)
  if (qualifierStripped !== candidate) {
    candidate = qualifierStripped
    if (EXERCISE_ALIAS_MAP[candidate]) return EXERCISE_ALIAS_MAP[candidate]
  }

  // 6. Cardio "maison" chiffré/annoté → entrée générique (dernier recours)
  const cardio = resolveCardioPrefix(candidate)
  if (cardio) return cardio

  // 7. Format de travail sans exercice attaché ("AMRAP 12 min", "EMOM 15 min
  // (alternance)"...) → collapse au mot-clé nu (voir NON_EXERCISE_LABELS)
  const workFormat = collapseWorkFormatLabel(candidate)
  if (workFormat) return workFormat

  return candidate
}

// ── Charge en kg depuis 1RM (athlete_profiles) ──────────────

export interface AthleteOneRm {
  rm_bench_press: number | null
  rm_back_squat: number | null
  rm_deadlift: number | null
  rm_overhead_press: number | null
  rm_pull_up: number | null
}

// Catégorie de normalisation pour l'agrégation future (tonnage/temps sous tension) —
// voir docs de décision produit sur la persistance de l'exécution des séances :
//  - external : charge externe réelle ou "haltère virtuel" (isolations BODYWEIGHT_RATIO_MAP)
//  - bodyweight_net : compounds bw:true (Pull-Up/Dips/Assisted Pull-Up...), déjà net
//    d'assistance ou majoré du lest — c'est CE nombre qu'il faut sommer, pas le poids
//    de corps brut, sous peine de compter un tirage assisté comme un développé couché.
//  - bodyweight_gross : mouvement au poids du corps sans équivalent externe (push-up,
//    burpee...) — poids du corps entier déplacé, à garder hors du tonnage principal.
//  - estimated_range : OPTION_B_SET, milieu de fourchette — confiance plus faible.
//  - not_applicable : cardio/durée, ou données insuffisantes (1RM/poids de corps manquant).
export type ChargeBasis =
  | "external"
  | "bodyweight_net"
  | "bodyweight_gross"
  | "estimated_range"
  | "not_applicable"

// Un "mouvement" de charge affichable — 1 par exercice simple, 1 par mouvement
// pour un combiné ("ExoA + ExoB" → 2 entrées, chacune avec son propre `label`).
export interface ChargeMovement {
  label?: string   // nom du mouvement — présent seulement pour un combiné (>1 movement)
  weight: string   // ligne 1 (gros/gras) : "42 kg" · "~13 kg par haltère" · "~12 kg de lest" ·
                    // "assistance ~8 kg" · "72 kg" (PDC nu avec bodyweightKg connu) · "Poids du corps" (PDC nu, bodyweightKg inconnu) ·
                    // "7-11 kg" (Option B) · charge brute inchangée (fallback)
  detail?: string   // ligne 2 (petit) : "63% 1RM" (RATIO_MAP) · "Poids du corps" (PDC nu avec bodyweightKg connu) ·
                    // "Charge modérée" (Option B) — absent en fallback
  weightKg: number | null // valeur numérique persistable pour le tonnage — null si non convertible
  basis: ChargeBasis
  perDumbbell?: boolean   // weightKg est le poids d'UN SEUL haltère (charge totale = ×2)
}

export interface ChargeDisplay {
  movements: ChargeMovement[]
  badge: "Charge calculée" | "Charge estimée" | null
}

/**
 * Matériel qui porte la charge — donc le plus petit écart réglable entre deux charges.
 *
 * Un %1RM se convertit en un réel, mais une salle ne délivre pas des réels : elle
 * délivre des crans. Arrondir au kg le plus proche, comme on le faisait partout,
 * invente des charges qui n'existent pas (7 kg sur une colonne de 5 en 5) et, sur une
 * série à paliers, écrase des paliers voisins sur la même valeur.
 */
export type LoadEquipment =
  /** Barre + disques : la plus petite paire fait 2 × 1.25 kg. */
  | "barbell"
  /** Râtelier d'haltères : écart croissant avec le poids (2 kg en bas, 5 kg en haut). */
  | "dumbbell"
  /** Poulie : colonne fine, souvent complétée d'une broche de 2.5 kg. */
  | "cable"
  /** Colonne de machine : plaques de 5 kg, sans demi-cran. */
  | "machine-stack"
  /** Poids du corps : aucun cran à respecter — c'est le lest, s'il y en a, qui se règle. */
  | "bodyweight"

interface RatioEntry {
  base: keyof AthleteOneRm
  ratio: number       // % du 1RM de base représentant le 1RM (ou poids système) de ce mouvement
  bw?: boolean        // le résultat est le poids total du système (poids de corps + lest)
  perDumbbell?: boolean // le résultat est le poids d'un seul haltère
  equipment: LoadEquipment
}

export type AthleteLevel = "beginner" | "intermediate" | "advanced"

// Isolations sans lien biomécanique avec un lift composé (S24-4) : le 1RM
// n'est plus dérivé d'un 1RM composé (multiplication de deux fractions non
// corrélées) mais estimé directement depuis le poids de corps, avec un ratio
// qui dépend du niveau de l'athlète.
interface BodyweightRatioEntry {
  base: "bodyweight"
  ratio: Record<AthleteLevel, number> // × poids de corps = 1RM estimé de ce mouvement
  perDumbbell?: boolean
  equipment: LoadEquipment
}

// Les 5 mouvements dont le 1RM est directement mesuré dans athlete_profiles —
// seuls ceux-là méritent le badge "Charge calculée" ; tout le reste (y compris
// les variantes à ratio 100, ex: Barbell Sumo Deadlift, Hip Thrust, Block Pull)
// est une estimation dérivée et affiche "Charge estimée".
export const MEASURED_BASE_LIFTS = new Set<string>([
  "Barbell Bench Press",
  "Barbell Overhead Press",
  "Pull-Up",
  "Back Squat",
  "Barbell Deadlift",
])

// Mouvement → 1RM de base + ratio. Ratio 100 sans flag = le mouvement de référence lui-même.
//
// ⚠️ Ces ratios sont des ESTIMATIONS, pas des données sourcées. Seuls les paliers de
// force des cinq mouvements de référence viennent d'ExRx (`src/data/strength_standards.json`,
// la traction étant une référence coach) ; aucun ratio de cette table n'en est tiré, et
// aucune source n'est consignée pour eux. Ils sont à valider au cas par cas par le coach
// au moment des premiers tests. Seul le ratio 100 d'un mouvement de référence est une
// mesure directe ; tout autre (ex. 38 % pour un développé ou un militaire à haltères,
// par haltère) est une valeur dérivée.
//
// Ils servent dans les DEUX sens, ce qui les rend d'autant plus sensibles : à convertir
// un %1RM en kg pour prescrire les charges (`computeChargeKg`), et à convertir un
// résultat de test en 1RM de référence (`referenceOneRmFromLift`, strengthTest.ts) —
// l'inverse exact du premier. Corriger un ratio ici déplace donc à la fois les charges
// prescrites et les 1RM que le test écrit dans `athlete_profiles`/`rm_history`. Les 1RM
// déjà enregistrés ne sont pas recalculés rétroactivement.
export const RATIO_MAP: Record<string, RatioEntry> = {
  // BASE : rm_bench_press
  "Barbell Bench Press":              { base: "rm_bench_press", ratio: 100, equipment: "barbell" },
  "Close-Grip Bench Press":           { base: "rm_bench_press", ratio: 87, equipment: "barbell" },
  "Close-Grip Dumbbell Press":        { base: "rm_bench_press", ratio: 35, perDumbbell: true, equipment: "dumbbell" },
  "Incline Barbell Bench Press":      { base: "rm_bench_press", ratio: 79, equipment: "barbell" },
  "Decline Barbell Bench Press":      { base: "rm_bench_press", ratio: 105, equipment: "barbell" },
  "JM Press":                         { base: "rm_bench_press", ratio: 55, equipment: "barbell" },
  "Dips":                             { base: "rm_bench_press", ratio: 102, bw: true, equipment: "bodyweight" },
  "Dumbbell Bench Press":             { base: "rm_bench_press", ratio: 38, perDumbbell: true, equipment: "dumbbell" },
  "Incline Dumbbell Press":           { base: "rm_bench_press", ratio: 32, perDumbbell: true, equipment: "dumbbell" },
  "Decline Dumbbell Press":           { base: "rm_bench_press", ratio: 40, perDumbbell: true, equipment: "dumbbell" },
  "Dumbbell Floor Press":             { base: "rm_bench_press", ratio: 35, perDumbbell: true, equipment: "dumbbell" },
  "Renegade Row":                     { base: "rm_bench_press", ratio: 20, perDumbbell: true, equipment: "dumbbell" },

  // BASE : rm_overhead_press
  "Barbell Overhead Press":           { base: "rm_overhead_press", ratio: 100, equipment: "barbell" },
  "Push Press":                       { base: "rm_overhead_press", ratio: 120, equipment: "barbell" },
  "Thruster":                         { base: "rm_overhead_press", ratio: 75, equipment: "barbell" },
  "Landmine Press":                   { base: "rm_overhead_press", ratio: 60, equipment: "barbell" },
  "KB Upright Row":                   { base: "rm_overhead_press", ratio: 55, equipment: "dumbbell" },
  "Dumbbell Shoulder Press":          { base: "rm_overhead_press", ratio: 38, perDumbbell: true, equipment: "dumbbell" },
  "Arnold Press":                     { base: "rm_overhead_press", ratio: 32, perDumbbell: true, equipment: "dumbbell" },

  // BASE : rm_pull_up
  "Pull-Up":                          { base: "rm_pull_up", ratio: 100, bw: true, equipment: "bodyweight" },
  "Chin-Up":                          { base: "rm_pull_up", ratio: 105, bw: true, equipment: "bodyweight" },
  "Assisted Pull-Up":                 { base: "rm_pull_up", ratio: 70, bw: true, equipment: "bodyweight" },
  "Lat Pulldown":                     { base: "rm_pull_up", ratio: 70, equipment: "cable" },
  "Bent-Over Row":                    { base: "rm_pull_up", ratio: 80, equipment: "barbell" },
  "Pendlay Row":                      { base: "rm_pull_up", ratio: 75, equipment: "barbell" },
  "Seated Cable Row":                 { base: "rm_pull_up", ratio: 65, equipment: "cable" },
  "T-Bar Row":                        { base: "rm_pull_up", ratio: 70, equipment: "barbell" },
  "Chest-Supported Row":              { base: "rm_pull_up", ratio: 60, equipment: "cable" },
  "Straight-Arm Pulldown":            { base: "rm_pull_up", ratio: 25, equipment: "cable" },
  "Dumbbell Row":                     { base: "rm_pull_up", ratio: 30, perDumbbell: true, equipment: "dumbbell" },
  "Bent-Over Dumbbell Row":           { base: "rm_pull_up", ratio: 30, perDumbbell: true, equipment: "dumbbell" },
  "Meadows Row":                      { base: "rm_pull_up", ratio: 28, perDumbbell: true, equipment: "dumbbell" },

  // BASE : rm_back_squat
  "Back Squat":                       { base: "rm_back_squat", ratio: 100, equipment: "barbell" },
  "Front Squat":                      { base: "rm_back_squat", ratio: 85, equipment: "barbell" },
  "Hack Squat":                       { base: "rm_back_squat", ratio: 90, equipment: "barbell" },
  "Leg Press":                        { base: "rm_back_squat", ratio: 160, equipment: "barbell" },
  "Power Clean":                      { base: "rm_back_squat", ratio: 66, equipment: "barbell" },
  "Step-Up":                          { base: "rm_back_squat", ratio: 46, equipment: "dumbbell" },
  "Goblet Squat":                     { base: "rm_back_squat", ratio: 60, equipment: "dumbbell" },
  "Bulgarian Split Squat":            { base: "rm_back_squat", ratio: 25, perDumbbell: true, equipment: "dumbbell" },
  "Walking Lunge":                    { base: "rm_back_squat", ratio: 20, perDumbbell: true, equipment: "dumbbell" },
  "Reverse Lunge":                    { base: "rm_back_squat", ratio: 20, perDumbbell: true, equipment: "dumbbell" },

  // BASE : rm_deadlift
  "Barbell Deadlift":                 { base: "rm_deadlift", ratio: 100, equipment: "barbell" },
  "Barbell Sumo Deadlift":            { base: "rm_deadlift", ratio: 100, equipment: "barbell" },
  "Block Pull":                       { base: "rm_deadlift", ratio: 110, equipment: "barbell" },
  "Romanian Deadlift":                { base: "rm_deadlift", ratio: 80, equipment: "barbell" },
  "Good Morning":                     { base: "rm_deadlift", ratio: 45, equipment: "barbell" },
  "Hip Thrust":                       { base: "rm_deadlift", ratio: 100, equipment: "barbell" },
  "Glute Bridge":                     { base: "rm_deadlift", ratio: 60, equipment: "barbell" },
  "Glute Bridge Walk":                { base: "rm_deadlift", ratio: 55, equipment: "barbell" },
  "Hyperextension":                   { base: "rm_deadlift", ratio: 40, equipment: "bodyweight" },
  "Reverse Hyperextension":           { base: "rm_deadlift", ratio: 30, equipment: "bodyweight" },
  "Cable Pull-Through":               { base: "rm_deadlift", ratio: 30, equipment: "cable" },
  "Kettlebell Swing":                 { base: "rm_deadlift", ratio: 30, equipment: "dumbbell" },
  "Dumbbell Romanian Deadlift":       { base: "rm_deadlift", ratio: 32, perDumbbell: true, equipment: "dumbbell" },
  // Amplitude complète depuis le sol, donc un peu plus lourd que le RDL à haltères —
  // mais toujours plafonné par la prise et par l'encombrement des deux haltères, d'où
  // un écart modeste. Ces deux entrées manquaient : les quatre programmes maison
  // Intermédiaire testent leur soulevé de terre là-dessus, et sans ratio le test ne
  // pouvait remonter aucun 1RM de référence (la charge y était déjà affichée brute).
  "Dumbbell Deadlift":                { base: "rm_deadlift", ratio: 35, perDumbbell: true, equipment: "dumbbell" },
  "Dumbbell Sumo Deadlift":           { base: "rm_deadlift", ratio: 35, perDumbbell: true, equipment: "dumbbell" },
  "Single-Leg RDL":                   { base: "rm_deadlift", ratio: 20, perDumbbell: true, equipment: "dumbbell" },
  "Farmer's Carry":                   { base: "rm_deadlift", ratio: 25, perDumbbell: true, equipment: "dumbbell" },
  "Suitcase Carry":                   { base: "rm_deadlift", ratio: 45, perDumbbell: true, equipment: "dumbbell" },
}

// Isolations sans lift composé biomécaniquement lié (S24-4) — 1RM estimé
// directement depuis le poids de corps, ratio dépendant du niveau athlète.
// Ratios : beginner / intermediate / advanced.
export const BODYWEIGHT_RATIO_MAP: Record<string, BodyweightRatioEntry> = {
  // Biceps
  "Hammer Curl":                      { base: "bodyweight", ratio: { beginner: 0.19, intermediate: 0.32, advanced: 0.44 }, perDumbbell: true, equipment: "dumbbell" },
  "Dumbbell Curl":                    { base: "bodyweight", ratio: { beginner: 0.18, intermediate: 0.30, advanced: 0.41 }, perDumbbell: true, equipment: "dumbbell" },
  "Cable Hammer Curl":                { base: "bodyweight", ratio: { beginner: 0.18, intermediate: 0.30, advanced: 0.41 }, perDumbbell: true, equipment: "cable" },
  "Spider Curl":                      { base: "bodyweight", ratio: { beginner: 0.17, intermediate: 0.28, advanced: 0.38 }, perDumbbell: true, equipment: "dumbbell" },
  "Concentration Curl":               { base: "bodyweight", ratio: { beginner: 0.14, intermediate: 0.24, advanced: 0.33 }, perDumbbell: true, equipment: "dumbbell" },
  "Incline Dumbbell Curl":            { base: "bodyweight", ratio: { beginner: 0.13, intermediate: 0.22, advanced: 0.30 }, perDumbbell: true, equipment: "dumbbell" },
  "Barbell Curl":                     { base: "bodyweight", ratio: { beginner: 0.48, intermediate: 0.80, advanced: 1.10 }, equipment: "barbell" },
  "Preacher Curl":                    { base: "bodyweight", ratio: { beginner: 0.48, intermediate: 0.80, advanced: 1.10 }, equipment: "barbell" },
  "EZ Bar Curl":                      { base: "bodyweight", ratio: { beginner: 0.43, intermediate: 0.72, advanced: 0.99 }, equipment: "barbell" },
  "Reverse Curl":                     { base: "bodyweight", ratio: { beginner: 0.42, intermediate: 0.70, advanced: 0.96 }, equipment: "barbell" },
  "Machine Biceps Curl":              { base: "bodyweight", ratio: { beginner: 0.38, intermediate: 0.64, advanced: 0.88 }, equipment: "machine-stack" },
  "Cable Biceps Curl":                { base: "bodyweight", ratio: { beginner: 0.33, intermediate: 0.56, advanced: 0.77 }, equipment: "cable" },

  // Triceps
  "Cable Triceps Pushdown":           { base: "bodyweight", ratio: { beginner: 0.47, intermediate: 0.80, advanced: 1.09 }, equipment: "cable" },
  "Skull Crusher":                    { base: "bodyweight", ratio: { beginner: 0.55, intermediate: 0.93, advanced: 1.27 }, equipment: "barbell" },
  "Overhead Triceps Extension":       { base: "bodyweight", ratio: { beginner: 0.47, intermediate: 0.80, advanced: 1.09 }, equipment: "dumbbell" },
  "Single-Arm Cable Triceps Pushdown": { base: "bodyweight", ratio: { beginner: 0.24, intermediate: 0.40, advanced: 0.55 }, equipment: "cable" },

  // Épaules
  "Dumbbell Lateral Raise":           { base: "bodyweight", ratio: { beginner: 0.11, intermediate: 0.19, advanced: 0.26 }, perDumbbell: true, equipment: "dumbbell" },
  "Cable Lateral Raise":              { base: "bodyweight", ratio: { beginner: 0.13, intermediate: 0.22, advanced: 0.30 }, equipment: "cable" },
  "Machine Lateral Raise":            { base: "bodyweight", ratio: { beginner: 0.13, intermediate: 0.22, advanced: 0.30 }, equipment: "machine-stack" },
  "Cable Face Pull":                  { base: "bodyweight", ratio: { beginner: 0.13, intermediate: 0.22, advanced: 0.30 }, equipment: "cable" },
  "Dumbbell Front Raise":             { base: "bodyweight", ratio: { beginner: 0.10, intermediate: 0.17, advanced: 0.23 }, perDumbbell: true, equipment: "dumbbell" },
  "Rear Delt Fly":                    { base: "bodyweight", ratio: { beginner: 0.09, intermediate: 0.15, advanced: 0.21 }, perDumbbell: true, equipment: "dumbbell" },

  // Pectoraux isolés
  "Pec Deck":                         { base: "bodyweight", ratio: { beginner: 0.38, intermediate: 0.64, advanced: 0.88 }, equipment: "machine-stack" },
  "Cable Fly":                        { base: "bodyweight", ratio: { beginner: 0.15, intermediate: 0.26, advanced: 0.35 }, equipment: "cable" },
  "Dumbbell Fly":                     { base: "bodyweight", ratio: { beginner: 0.11, intermediate: 0.19, advanced: 0.26 }, perDumbbell: true, equipment: "dumbbell" },
  "Incline Dumbbell Fly":             { base: "bodyweight", ratio: { beginner: 0.10, intermediate: 0.17, advanced: 0.24 }, perDumbbell: true, equipment: "dumbbell" },

  // Jambes isolées
  "Leg Extension":                    { base: "bodyweight", ratio: { beginner: 0.57, intermediate: 0.96, advanced: 1.31 }, equipment: "machine-stack" },
  "Lying Leg Curl":                   { base: "bodyweight", ratio: { beginner: 0.47, intermediate: 0.80, advanced: 1.09 }, equipment: "machine-stack" },
  "Seated Leg Curl":                  { base: "bodyweight", ratio: { beginner: 0.47, intermediate: 0.80, advanced: 1.09 }, equipment: "machine-stack" },
  "Standing Calf Raise":              { base: "bodyweight", ratio: { beginner: 0.76, intermediate: 1.29, advanced: 1.76 }, equipment: "machine-stack" },
  "Seated Calf Raise":                { base: "bodyweight", ratio: { beginner: 0.57, intermediate: 0.97, advanced: 1.32 }, equipment: "machine-stack" },
  "Single-Leg Calf Raise":            { base: "bodyweight", ratio: { beginner: 0.38, intermediate: 0.64, advanced: 0.88 }, equipment: "bodyweight" },
}

// Core / machine isolation / élastique — pas de 1RM pertinent, fourchette % poids de corps.
export const OPTION_B_SET = new Set<string>([
  "Cable Crunch", "Cable Woodchopper", "Pallof Press", "Russian Twist", "Cable Kickback",
  "Seated Hip Abduction", "Seated Hip Adduction", "Band Face Pull", "Band Lateral Raise",
  "Band Pull-Apart", "Band Pulldown", "Band Row", "Band Chest Fly", "Band Biceps Curl",
  "Band Triceps Pushdown", "Band Woodchopper", "TKE élastique",
])

// PDC / cardio — exercices sans charge externe (poids de corps ou équipement cardio),
// aucune conversion en kg n'est pertinente. Référencé pour l'audit (script diagnose) ;
// computeChargeKg les laisse déjà inchangés faute d'entrée dans RATIO_MAP/OPTION_B_SET.
export const NO_CHARGE_SET = new Set<string>([
  "Push-Up", "Decline Push-Up", "Diamond Push-Up", "Inverted Row", "Bench Dips",
  "Sissy Squat", "Bodyweight Squat", "Squat Jump", "Nordic Curl", "Slider Leg Curl",
  "Glute-Ham Raise", "Clamshell", "Bird-Dog", "Superman Hold", "Ab Wheel Rollout",
  "Hanging Leg Raise", "Dragon Flag", "Hollow Body Hold", "L-Sit Hold", "Bear Plank Hold",
  "Plank", "Side Plank", "Dead Bug", "V-Up", "Bicycle Crunch", "Plank to Downdog",
  "Burpee", "Box Jump", "Depth Jump", "Jump Rope", "Jumping Jack", "High Knee Skips",
  "Skater Jumps", "Mountain Climbers", "Air Bike", "Wall Ball", "Course / Tapis de course",
  "Ski Erg", "Sled Push", "Sled Pull", "MB Supine Chest Throw", "DB Complex",
  "Rameur", "Vélo",
])

// Sous-ensemble de NO_CHARGE_SET : machines/mouvements cycliques mesurés en durée/
// distance, sans notion de répétition discrète — contrairement aux mouvements au
// poids du corps répétition par répétition (Push-Up, Burpee, Jump Rope...) qui
// restent classés `bodyweight_gross`, ceux-ci n'ont aucune estimation de charge
// pertinente : le corps est porté en continu (assise/appui), pas déplacé à
// chaque répétition individuelle. Jump Rope est volontairement absent d'ici
// (chaque saut déplace tout le poids du corps, comme Push-Up/Burpee) malgré son
// unité de mesure en durée — le critère est le mouvement, pas l'unité affichée.
export const CARDIO_NO_LOAD_SET = new Set<string>([
  "Air Bike", "Course / Tapis de course", "Ski Erg",
  "Sled Push", "Sled Pull", "Rameur", "Vélo",
])

// Un pourcentage isolé ("70%") ou une fourchette ("55-70%", "60–65 %") — la
// fourchette est résolue à sa MOYENNE EXACTE (62.5 reste 62.5 pour le calcul du
// kg ; seul le kg final est arrondi). Le % affiché est cette même moyenne,
// arrondie à l'entier le plus proche pour l'affichage — jamais le haut de la
// fourchette, qui surestimerait la charge.
const RANGE_PCT_RE = /(\d+(?:[.,]\d+)?)\s*[-–—]\s*(\d+(?:[.,]\d+)?)\s*%/
const SINGLE_PCT_RE = /(\d+(?:[.,]\d+)?)\s*%/

// Charge à paliers : "70% → 75% → 80% 1RM", "70→75→82% 1RM", "7-8RM → -10% → 3RM".
// La flèche n'est PAS un séparateur de fourchette — c'est une suite de charges
// successives à l'intérieur d'une même série.
const PALIER_SEP_RE = /\s*(?:→|->)\s*/
// Palier "nu" : un nombre (ou une fourchette) sans "%", parce que le "%" de la suite
// n'est écrit qu'une fois, sur le dernier palier ("70→75→82% 1RM").
const BARE_NUMBER_RE = /^\s*\d+(?:[.,]\d+)?(?:\s*[-–—]\s*\d+(?:[.,]\d+)?)?\s*$/

const toNum = (s: string) => parseFloat(s.replace(",", "."))

/**
 * Pourcentage de 1RM porté par une charge, et le qualificatif qui le suit ("1RM",
 * "max"...).
 *
 * Sur une charge à paliers, c'est le PREMIER palier qui est résolu — la charge à
 * laquelle le client attaque la série. Les paliers suivants ne sont pas représentables
 * par la tuile Charge, qui n'affiche qu'une valeur ; ils relèvent du schéma de série.
 *
 * Chercher un "%" n'importe où dans la chaîne, comme avant, faisait remonter un palier
 * arbitraire : le dernier quand la suite n'écrit le "%" qu'une fois ("70→75→82% 1RM"
 * → 82), et surtout un incrément relatif quand il y en a un ("7-8RM → -10% → 3RM"
 * → 10, soit une charge servie à 10 % du 1RM).
 *
 * Un premier palier qui n'est pas un pourcentage absolu (un nombre de reps-max
 * "7-8RM", un "PDC") renvoie null : la charge s'affiche alors brute, telle que le
 * programme l'a écrite, plutôt que convertie en un kg faux.
 */
function resolvePercent(charge: string): { pct: number; suffix: string } | null {
  const paliers = charge.split(PALIER_SEP_RE)
  return paliers.length > 1 ? resolveFirstPalier(paliers) : resolveSinglePercent(charge)
}

function resolveFirstPalier(paliers: string[]): { pct: number; suffix: string } | null {
  const head = paliers[0]
  // Le premier palier porte son propre "%" ("70% 1RM → +5% → +10%").
  const own = resolveSinglePercent(head)
  if (own) return own
  // Sinon : suite de nombres nus dont seul le dernier palier porte le "%"
  // ("70→75→82% 1RM"). Ce "%" vaut pour toute la suite, donc pour le premier — mais
  // seulement si le premier est bien un nombre nu, et pas autre chose ("7-8RM", "PDC").
  if (!BARE_NUMBER_RE.test(head)) return null
  const tail = resolveSinglePercent(paliers[paliers.length - 1])
  if (!tail) return null
  const range = /^\s*(\d+(?:[.,]\d+)?)\s*[-–—]\s*(\d+(?:[.,]\d+)?)\s*$/.exec(head)
  const pct = range ? (toNum(range[1]) + toNum(range[2])) / 2 : toNum(head)
  return { pct, suffix: tail.suffix }
}

function resolveSinglePercent(charge: string): { pct: number; suffix: string } | null {
  const range = RANGE_PCT_RE.exec(charge)
  if (range) {
    return {
      pct: (toNum(range[1]) + toNum(range[2])) / 2,
      suffix: percentSuffix(charge, range.index + range[0].length),
    }
  }
  const single = SINGLE_PCT_RE.exec(charge)
  if (single) {
    return { pct: toNum(single[1]), suffix: percentSuffix(charge, single.index + single[0].length) }
  }
  return null
}

// Reprend le qualificatif qui suit le "%" dans la charge JSON d'origine ("1RM",
// "max"...) pour l'accoler au % moyenné affiché — s'arrête au prochain séparateur.
function percentSuffix(charge: string, matchEnd: number): string {
  const rest = charge.slice(matchEnd)
  const m = /^\s*([^·(/+—–\n]*)/.exec(rest)
  return m ? m[1].trim() : ""
}

function formatPercentDetail(pctRounded: number, suffix: string): string {
  return suffix ? `${pctRounded}% ${suffix}` : `${pctRounded}%`
}

// Formats "poids de corps" directs — ne nécessitent NI %1RM NI oneRm, juste le
// poids de corps (le cas échéant) :
//  - "PDC" (éventuellement suivi d'une note entre parenthèses) → poids du corps.
//  - "PDC / 65% 1RM" → alternative, le "/" isole une note secondaire ; on garde
//    le choix par défaut (PDC), comme pour les noms d'exercice ("A / B" → A).
//  - "PDC + 15 kg" / "PDC + 15-20 kg" → lest donné directement en kg (moyenné si
//    fourchette), sans passer par le ratio/1RM.
//  - "PDC (assisté 20%)" / "Assistance 50% PDC" → assistance = ce % du poids de corps.
const PDC_PLUS_KG_RE = /^\s*PDC\s*\+\s*(\d+(?:[.,]\d+)?)\s*(?:[-–—]\s*(\d+(?:[.,]\d+)?)\s*)?kg/i
const PDC_ASSISTED_PCT_RE = /assist[ée]e?\D{0,10}(\d+(?:[.,]\d+)?)\s*%|assistance\D{0,10}(\d+(?:[.,]\d+)?)\s*%/i
const PDC_BARE_RE = /^\s*PDC\s*(\(.*\))?\s*$/i
const PDC_ALT_RE = /^\s*PDC\s*\//i

function resolveBodyweightChargeDirect(
  charge: string,
  bodyweightKg: number | null
): ChargeMovement | null {
  const plusKg = PDC_PLUS_KG_RE.exec(charge)
  if (plusKg) {
    const lo = parseFloat(plusKg[1].replace(",", "."))
    const hi = plusKg[2] ? parseFloat(plusKg[2].replace(",", ".")) : lo
    const lestKg = Math.round((lo + hi) / 2)
    return {
      weight: `~${lestKg} kg de lest`,
      weightKg: bodyweightKg != null ? Math.round(bodyweightKg + lestKg) : null,
      basis: "bodyweight_net",
    }
  }

  if (bodyweightKg != null) {
    const assisted = PDC_ASSISTED_PCT_RE.exec(charge)
    if (assisted) {
      const pct = parseFloat((assisted[1] ?? assisted[2]).replace(",", "."))
      const assistKg = Math.round(bodyweightKg * pct / 100)
      return {
        weight: `assistance ~${assistKg} kg`,
        weightKg: Math.round(bodyweightKg - assistKg),
        basis: "bodyweight_net",
      }
    }
  }

  if (PDC_BARE_RE.test(charge) || PDC_ALT_RE.test(charge)) {
    return bodyweightKg != null
      ? { weight: `${Math.round(bodyweightKg)} kg`, detail: "Poids du corps", weightKg: Math.round(bodyweightKg), basis: "bodyweight_net" }
      : { weight: "Poids du corps", weightKg: null, basis: "not_applicable" }
  }

  return null
}

function computeChargeKgSingle(
  rawName: string,
  charge: string,
  oneRm: AthleteOneRm | null,
  bodyweightKg: number | null,
  level: AthleteLevel
): ChargeMovement & { badge: ChargeDisplay["badge"] } {
  const unchanged = {
    weight: charge, weightKg: null as number | null, basis: "not_applicable" as ChargeBasis,
    badge: null as ChargeDisplay["badge"],
  }
  const canonical = normalizeExerciseName(rawName)
  const entry = RATIO_MAP[canonical]
  const bwEntry = BODYWEIGHT_RATIO_MAP[canonical]

  if (bwEntry) {
    if (bodyweightKg == null) return unchanged
    const resolved = resolvePercent(charge)
    if (!resolved) return unchanged
    const { pct } = resolved
    const detail = formatPercentDetail(Math.round(pct), resolved.suffix)
    const estimated1Rm = bodyweightKg * bwEntry.ratio[level]
    const kg = Math.round(estimated1Rm * (pct / 100))
    const badge: ChargeDisplay["badge"] = "Charge estimée"
    if (bwEntry.perDumbbell) return { weight: `~${kg} kg par haltère`, detail, weightKg: kg, basis: "external", perDumbbell: true, badge }
    return { weight: `~${kg} kg`, detail, weightKg: kg, basis: "external", badge }
  }

  // Formats "poids de corps" directs — pas besoin de %1RM ni de oneRm.
  if (entry?.bw) {
    const direct = resolveBodyweightChargeDirect(charge, bodyweightKg)
    if (direct) return { ...direct, badge: null }
  }

  // PDC/cardio sans entrée de ratio map : leur charge JSON est un "PDC" brut, sans
  // "%" — resolvePercent renverrait toujours null pour eux et les ferait tomber
  // dans le fallback `unchanged` ci-dessous sans jamais atteindre ces branches.
  // Doivent donc être vérifiés AVANT la résolution de pourcentage, contrairement à
  // OPTION_B_SET qui, lui, a réellement besoin d'un % à résoudre.
  if (CARDIO_NO_LOAD_SET.has(canonical)) {
    return { weight: charge, weightKg: null, basis: "not_applicable", badge: null }
  }
  if (NO_CHARGE_SET.has(canonical)) {
    return { weight: charge, weightKg: bodyweightKg != null ? Math.round(bodyweightKg) : null, basis: "bodyweight_gross", badge: null }
  }

  const resolved = resolvePercent(charge)
  if (!resolved) return unchanged
  const { pct } = resolved // moyenne exacte — utilisée pour le calcul du kg
  const detail = formatPercentDetail(Math.round(pct), resolved.suffix)

  if (entry) {
    const rm = oneRm?.[entry.base] ?? null
    if (!rm) return unchanged

    const badge: ChargeDisplay["badge"] = MEASURED_BASE_LIFTS.has(canonical) ? "Charge calculée" : "Charge estimée"

    if (entry.bw) {
      if (bodyweightKg == null) return unchanged
      const total = (rm * entry.ratio / 100) * (pct / 100)
      const diff = Math.round(total - bodyweightKg)
      const weightKg = Math.round(total)
      if (Math.abs(diff) < 2) return { weight: "Poids du corps", detail, weightKg, basis: "bodyweight_net", badge }
      if (diff > 0) return { weight: `~${diff} kg de lest`, detail, weightKg, basis: "bodyweight_net", badge }
      return { weight: `assistance ~${Math.abs(diff)} kg`, detail, weightKg, basis: "bodyweight_net", badge }
    }

    const kg = Math.round((rm * entry.ratio / 100) * (pct / 100))
    if (entry.perDumbbell) return { weight: `~${kg} kg par haltère`, detail, weightKg: kg, basis: "external", perDumbbell: true, badge }
    const prefix = badge === "Charge calculée" ? "" : "~"
    return { weight: `${prefix}${kg} kg`, detail, weightKg: kg, basis: "external", badge }
  }

  if (OPTION_B_SET.has(canonical)) {
    if (bodyweightKg == null) return unchanged
    let low: number, high: number, label: string
    if (pct < 60) { low = 5; high = 10; label = "légère" }
    else if (pct <= 75) { low = 10; high = 15; label = "modérée" }
    else { low = 15; high = 20; label = "lourde" }
    const kgLow = Math.round(bodyweightKg * low / 100)
    const kgHigh = Math.round(bodyweightKg * high / 100)
    return { weight: `${kgLow}-${kgHigh} kg`, detail: `Charge ${label}`, weightKg: Math.round((kgLow + kgHigh) / 2), basis: "estimated_range", badge: null }
  }

  return unchanged
}

/**
 * Résout la charge affichée pour un exercice : conversion du %1RM en kg (mouvements
 * de force), fourchette % poids de corps (isolation/élastique), ou inchangé (PDC/cardio,
 * ou données manquantes). Gère les exercices combinés ("ExoA + ExoB") en calculant
 * chaque mouvement séparément — un `ChargeMovement` par mouvement, avec son `label`.
 */
/**
 * Découpe un champ combiné ("A + B") selon la même convention que la charge :
 * séparateur " + " ou " / " s'il est présent, sinon la valeur brute dupliquée
 * pour chaque mouvement (positionnel, dans l'ordre des noms). Utilisé pour la
 * charge (en interne) et pour les reps par mouvement (persistance par série).
 */
export function splitCombinedField(field: string, count: number): string[] {
  if (field.includes(" + ")) return field.split(" + ").map(s => s.trim())
  if (field.includes(" / ")) return field.split(" / ").map(s => s.trim())
  return Array(count).fill(field)
}

/**
 * Nombre de séries prescrites — extrait l'entier de tête ("4" → 4, "3 RP" → 3,
 * "1 cycle complet" → 1) ; retombe sur 1 si rien d'exploitable (protocoles
 * cluster/EMOM/test sans entier de tête, jamais un total inventé plus haut).
 */
export function parseSetsCount(sets: string): number {
  return parseInt(sets, 10) || 1
}

/**
 * Matériel d'un mouvement. `null` quand il n'est dans aucune des deux tables de ratio
 * (PDC, cardio, isolation Option B) — l'appelant n'a alors pas de cran à respecter.
 */
export function resolveEquipment(rawExerciseName: string): LoadEquipment | null {
  const canonical = normalizeExerciseName(rawExerciseName)
  return RATIO_MAP[canonical]?.equipment ?? BODYWEIGHT_RATIO_MAP[canonical]?.equipment ?? null
}

/**
 * Plus petit écart réglable, au voisinage d'une charge donnée.
 *
 * Seul le râtelier d'haltères dépend de la charge : les petits haltères vont de 2 en 2,
 * les gros de 5 en 5. Un même schéma de série utilise un cran UNIQUE, calculé une fois
 * sur le palier d'ancrage — sinon deux paliers voisins pourraient viser des grilles
 * différentes et se croiser.
 */
export function equipmentStepKg(eq: LoadEquipment, aroundKg: number): number {
  switch (eq) {
    case "barbell":       return 2.5
    case "cable":         return 2.5
    case "machine-stack": return 5
    case "dumbbell":      return aroundKg < 10 ? 2 : aroundKg < 30 ? 2.5 : 5
    case "bodyweight":    return 1
  }
}

/** Arrondi au cran le plus proche, en gardant une seule décimale (crans de 2.5). */
function snap(kg: number, step: number): number {
  return Math.round(Math.round(kg / step) * step * 10) / 10
}

/**
 * Arrondit les paliers d'une série au cran de matériel, avec au moins un cran d'écart
 * entre deux paliers consécutifs dont les pourcentages diffèrent.
 *
 * Le problème : sur une isolation légère, trois paliers à 70/75/82 % d'un 1RM de 6 kg
 * donnent 4.2 / 4.5 / 4.9 kg, soit "4, 4, 4" une fois arrondis au kg. Trois paliers
 * identiques ne sont pas une méthode d'intensification, c'est trois fois la même série.
 *
 * L'ancrage se fait sur le palier LE PLUS LOURD, et les autres sont poussés à partir de
 * lui. Sur une méthode d'intensification, mieux vaut dériver en dessous de la charge
 * prévue qu'au-dessus : le palier qui compte — celui qui porte le stimulus — tombe
 * juste, et l'écart est absorbé par les paliers légers.
 *
 * L'ordre voulu est lu sur les POURCENTAGES, jamais sur les kg arrondis : c'est
 * exactement parce que les kg s'écrasent qu'on ne peut pas leur demander l'ordre.
 * Deux pourcentages égaux gardent délibérément la même charge ("95% → 95%").
 *
 * Seule exception au principe « jamais au-dessus » : une suite non monotone où un
 * palier extérieur est plus lourd que son voisin vers l'ancre ("77 → 69 → 87") peut
 * devoir remonter d'un cran pour rester distinct de ce voisin. Il reste sous l'ancre.
 */
export function roundPalierSeries(
  percents: Array<number | null>,
  oneRmKg: number,
  eq: LoadEquipment
): Array<number | null> {
  const out: Array<number | null> = percents.map(() => null)
  const known = percents
    .map((p, i) => ({ p, i }))
    .filter((x): x is { p: number; i: number } => x.p != null)
  if (known.length === 0) return out

  // Ancre = le palier le plus lourd. À égalité, le premier — c'est celui que le client
  // atteint en premier, et l'ancre doit être stable quel que soit le reste de la suite.
  const anchor = known.reduce((best, x) => (x.p > best.p ? x : best), known[0])
  const ideal = (p: number) => (oneRmKg * p) / 100
  const step = equipmentStepKg(eq, ideal(anchor.p))
  out[anchor.i] = snap(ideal(anchor.p), step)

  // Puis on s'éloigne de l'ancre dans les deux sens, chaque palier étant contraint par
  // son voisin CÔTÉ ANCRE, seul déjà fixé.
  const walk = (from: number, to: number, dir: 1 | -1) => {
    for (let i = from; dir > 0 ? i <= to : i >= to; i += dir) {
      const pct = percents[i]
      const ref = out[i - dir]
      const refPct = percents[i - dir]
      if (pct == null) continue
      if (ref == null || refPct == null) { out[i] = snap(ideal(pct), step); continue }
      const target = snap(ideal(pct), step)
      if (pct < refPct)      out[i] = Math.min(target, Math.round((ref - step) * 10) / 10)
      else if (pct > refPct) out[i] = Math.max(target, Math.round((ref + step) * 10) / 10)
      else                   out[i] = ref
    }
  }
  walk(anchor.i - 1, 0, -1)
  walk(anchor.i + 1, percents.length - 1, 1)

  // Pousser vers le bas peut sortir de la grille par le bas : sur une isolation dont le
  // 1RM estimé est de quelques kilos, trois paliers distincts ne TIENNENT pas sous
  // l'ancre (72 % de 5 kg, moins deux crans de 2 kg, tombe à zéro). La série entière
  // remonte alors du strict nécessaire, d'un nombre entier de crans : l'écart d'un cran
  // est préservé, et la dérive vers le haut reste la plus petite possible.
  //
  // C'est la seule situation où l'ancre s'éloigne de sa charge prévue, et elle dit
  // quelque chose de réel : à ce niveau de charge, le matériel ne sait pas rendre le
  // protocole demandé. `migrate-set-schemes.js` signale ces exercices à la relecture.
  const lowest = out.reduce<number | null>((min, kg) => (kg == null ? min : min == null ? kg : Math.min(min, kg)), null)
  if (lowest == null || lowest >= step) return out
  const lift = Math.ceil((step - lowest) / step) * step
  return out.map(kg => (kg == null ? null : Math.round((kg + lift) * 10) / 10))
}

export function computeChargeKg(
  rawExerciseName: string,
  charge: string,
  oneRm: AthleteOneRm | null,
  bodyweightKg: number | null,
  level: AthleteLevel = "beginner"
): ChargeDisplay {
  if (rawExerciseName.includes(" + ")) {
    const names = rawExerciseName.split(" + ").map(s => s.trim())
    // Convention JSON : la charge d'un combiné est jointe par " + " (rare) ou,
    // le plus souvent, par " / " (positionnel — 1 tranche par mouvement, dans le
    // même ordre que le nom : "PDC / 65% 1RM" pour "Dips + Arnold Press" veut dire
    // Dips=PDC, Arnold Press=65% 1RM). Sans séparateur, on partage la charge brute
    // entre les deux (dernier recours, imprécis).
    const charges = splitCombinedField(charge, names.length)
    const results = names.map((n, i) => computeChargeKgSingle(n, charges[i] ?? charge, oneRm, bodyweightKg, level))
    return {
      movements: results.map((r, i) => ({
        label: names[i], weight: r.weight, detail: r.detail,
        weightKg: r.weightKg, basis: r.basis, perDumbbell: r.perDumbbell,
      })),
      badge: results.find(r => r.badge)?.badge ?? null,
    }
  }
  const r = computeChargeKgSingle(rawExerciseName, charge, oneRm, bodyweightKg, level)
  return {
    movements: [{ weight: r.weight, detail: r.detail, weightKg: r.weightKg, basis: r.basis, perDumbbell: r.perDumbbell }],
    badge: r.badge,
  }
}
